/**
 * Submittal Builder JS.
 *
 * Localized vars available as vtcSBVars.
 */

/*
 *	Merged PDFs download functionality.
 */

import resourcesCart from './resourcesCart';
import selectors from './selectors';

const endpoint = '/wp-json/VTC/v1/merged-pdf/';
const slugs = {
  pending: 'vtcPendingPDFsQueue',
  ready: 'vtcReadyPDFsQueue',
};
const queues = {
  pending: JSON.parse(window.localStorage.getItem(slugs.pending)) || [],
  ready: JSON.parse(window.localStorage.getItem(slugs.ready)) || [],
};
let pdfDownloadReady = null;
let $container;
let handleAsyncDownloadXhr;

const icons = {
	failure: '<span class="icon icon-caution-red"></span>',
	success: '<span class="icon icon-check-green"></span>',
	loading: '<span class="icon icon-spinner"></span>',
};

/**
 * Function to process the queue of PDFs that are ready to download.
 */
const processReadyPDFsQueue = () => {
	// Wait for the previous file to be downloaded before changing the download link.
	// Also, don't continue if the are no downloads.
	if (pdfDownloadReady || !queues.ready || queues.ready.length === 0) {
		return;
	}

	// Get 1st element in queue and remove it.
	const hash = queues.ready.shift();

	// Trigger download.
	window.location = `${endpoint}download/${hash}`;

	// Clear PDF download ready status to allow for another download notification to be displayed.
	pdfDownloadReady = null;

	if (queues.ready.length === 0) {
		// Remove the element from local storage.
		window.localStorage.removeItem(slugs.ready);
	} else {
		// Save the array to local storage.
		window.localStorage.setItem(slugs.ready, JSON.stringify(queues.ready));

		// Process the next element in the queue (if any).
		processReadyPDFsQueue();
	}

	// Mark the download as ready, so that the other downloads wait before the current one is downloaded.
	pdfDownloadReady = true;

	if ($container) {
		$container.find('span').replaceWith(icons.success);
	}
};

/**
 * Function to remove hash from pending queue.
 *
 * @param {String} hash
 */
const removeHashFromPendingQueue = (hash) => {
	queues.pending = queues.pending.filter((h) => h !== hash);

	if (queues.pending.length === 0) {
		// Remove the element from local storage.
		window.localStorage.removeItem(slugs.pending);
	} else {
		// Save the array to local storage.
		const data = JSON.stringify(queues.pending);
		window.localStorage.setItem(slugs.pending, data);
	}
};

/**
 * Function to add a hash to the queue of PDFs that are ready to download.
 *
 * @param {String} hash Hash string.
 */
const addToReadyPDFsQueue = (hash) => {
  removeHashFromPendingQueue(hash);

  // Add file hash to queue to be processed later.
  queues.ready.push(hash);

  // Save the array to local storage.
  const data = JSON.stringify(queues.ready);
  window.localStorage.setItem(slugs.ready, data);

  // Process queue of PDFs to download.
  processReadyPDFsQueue();
};

/**
 * Function to check
 *
 * @param {Array} hashes An array of hashes.
 */
const handleAsyncDownload = (hashes) => {
	// Abort previous request.
	if (handleAsyncDownloadXhr) {
		handleAsyncDownloadXhr.abort();
	}

	handleAsyncDownloadXhr = $.post({
			url: `${endpoint}status`,
			cache: false,
			data: {
			hashes,
		},
		success: (data) => {
			if (!data || !data.length) {
				return;
			}

			const errors = [];

			for (let i = 0; i < data.length; i += 1) {
				if (data[i].status && data[i].status === 'completed') {
					// If PDF files were merged correctly, add the merged file to the download queue.
					addToReadyPDFsQueue(data[i].hash);

					// Handle other items from the queue (if any) in the next cycle -
					// when processPendingPDFsQueue gets executed.
					break;
				}

				// Process errors.
				if (data[i].status && data[i].status === 'failed' && data[i].message) {
					// Remove current pending PDF from queue.
					removeHashFromPendingQueue(data[i].hash);

					if ($container) {

						$container.find('span').replaceWith(icons.failure);

						const $message = $('<p></p>').text(data[i].message).css('text-align', 'center');

						$container.after($message);

					} else {
						errors.push(data[i].message);
					}
				}
			}

			if (errors.length) {
				// eslint-disable-next-line no-alert
				alert(errors.join('\n'));
			}

			// Schedule another sync in 5 seconds, if there are still elements in the pending PDFs queue.
			if (queues.pending.length > 0) {
				// eslint-disable-next-line no-use-before-define
				setTimeout(processPendingPDFsQueue, 5000);
			}
		},
	});
};

/**
 * Function to process the queue of PDFs that are still in the merging process.
 */
const processPendingPDFsQueue = () => {
	// Don't continue if the are no pending PDFs.
	if (queues.pending.length !== 0) {
		// Start sync immediately.
		handleAsyncDownload(queues.pending);
	}
};

/**
 * Function to add a hash to the queue of PDFs that are in the merging process.
 *
 * @param {String} hash Hash string.
 */
const addToPendingPDFsQueue = (hash) => {
	// Add file hash to queue to be processed later.
	queues.pending.push(hash);

	// Save the array to local storage.
	const data = JSON.stringify(queues.pending);
	window.localStorage.setItem(slugs.pending, data);

	// Process queue of PDFs to push to download.
	processPendingPDFsQueue();
};

const startMergedPDFAsyncDownload = (hash) => {
	const { form } = selectors;
	if (!hash) {
		return;
	}

	addToPendingPDFsQueue(hash);

	$container = $('<div></div>')
		.addClass(form.status.replace('.', ''))
		.html(icons.loading);

	$(form.text).hide();
	$(form.confirmation).append($container);

	resourcesCart.emptyCart();
};

const SubmittalBuilder = {
	processPendingPDFsQueue,
	processReadyPDFsQueue,
	startMergedPDFAsyncDownload,
};

export default SubmittalBuilder;
